import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

const AuthPage = ({ mode }) => {
  const isLogin = mode === "login";
  const { t } = useLang();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/members";
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form.email, form.password, form.name);
      navigate(from);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={`${mode}-page`} className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/sereva/media/77330fd5-ea3b-4a10-b021-17f588293d07.jpeg`} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/40" />
        <div className="absolute bottom-12 left-12 text-primary-foreground">
          <p className="font-serif text-5xl tracking-tightest">Nordicvy</p>
          <p className="mt-2 opacity-80">{t("footer.tagline")}</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-24">
        <div className="w-full max-w-sm">
          <Link to="/" className="font-serif text-3xl text-primary tracking-tightest lg:hidden block mb-10">Nordicvy</Link>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tightest text-foreground mb-10">
            {isLogin ? t("auth.loginTitle") : t("auth.registerTitle")}
          </h1>

          <form onSubmit={submit} className="space-y-5">
            {!isLogin && (
              <input data-testid="auth-name" required placeholder={t("auth.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-muted px-4 py-3.5 outline-none focus:ring-1 focus:ring-primary" />
            )}
            <input data-testid="auth-email" type="email" required placeholder={t("auth.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-muted px-4 py-3.5 outline-none focus:ring-1 focus:ring-primary" />
            <input data-testid="auth-password" type="password" required placeholder={t("auth.password")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full bg-muted px-4 py-3.5 outline-none focus:ring-1 focus:ring-primary" />
            {error && <p data-testid="auth-error" className="text-destructive text-sm">{error}</p>}
            <button data-testid="auth-submit" disabled={loading} className="w-full bg-primary text-primary-foreground py-4 text-sm font-medium flex items-center justify-center gap-2 hover:bg-primary/90 active:scale-[0.98] transition-[transform,background-color] duration-200 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isLogin ? t("auth.loginBtn") : t("auth.registerBtn")}
            </button>
          </form>

          <Link data-testid="auth-switch" to={isLogin ? "/register" : "/login"} className="block mt-6 text-sm text-foreground/60 hover:text-primary transition-colors link-underline w-fit">
            {isLogin ? t("auth.toRegister") : t("auth.toLogin")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
